export class FloorCropModal {
  /**
   * @param {(croppedDataURL: string, realW: number|null, realH: number|null) => void} onApply
   * @param {(key: string, fallback: string) => string} [t] - optional translation function
   */
  constructor(onApply, t) {
    this.onApply = onApply;
    this.t = t ?? ((key, fallback) => fallback);

    this.modal = document.getElementById("floor-crop-modal");
    this.canvas = document.getElementById("floor-crop-canvas");
    this.ctx = this.canvas ? this.canvas.getContext("2d") : null;

    this.image = null;
    this.imgRect = { x: 0, y: 0, w: 0, h: 0 };
    this.crop = { x: 0, y: 0, w: 0, h: 0 };
    this.drag = null;

    // Scale reference line state
    this.scaleMode   = false;  // true while user is picking reference points
    this.scalePt1    = null;   // first clicked point (canvas coords)
    this.scalePt2    = null;   // second clicked point
    this.scalePreview = null;  // live mouse position while picking pt2
    this.pxPerMeter  = null;   // computed: canvas pixels per 1 real-world metre

    this._bindEvents();
  }

  // ── Public API ────────────────────────────────────────────────────────────

  open(dataURL) {
    const img = new Image();
    img.onload = () => {
      this.image = img;
      this._sizeCanvas();
      this._initCrop();
      this._draw();
      this._setSubtitle(this.t("designerFloorCropScaleRequired", "Set a scale reference to continue"));
      this._updateApplyBtn();
      this.modal.classList.add("modal-visible");
    };
    img.src = dataURL;
  }

  close() {
    this.modal.classList.remove("modal-visible");
    this.image = null;
    this.drag = null;
    this._resetScale();
    document.getElementById("floor-crop-dim-w").style.display = "none";
    document.getElementById("floor-crop-dim-h").style.display = "none";
  }

  // ── Private ───────────────────────────────────────────────────────────────

  _sizeCanvas() {
    const wrapper = this.canvas.parentElement;
    const maxW = Math.min(760, wrapper.clientWidth || 760);
    const ratio = this.image.naturalHeight / this.image.naturalWidth;
    const maxH = Math.floor(window.innerHeight * 0.6);
    let w = maxW;
    let h = Math.round(w * ratio);
    if (h > maxH) { h = maxH; w = Math.round(h / ratio); }
    this.canvas.width = w;
    this.canvas.height = h;

    const imgRatio = this.image.naturalWidth / this.image.naturalHeight;
    const canRatio = w / h;
    let iw, ih, ix, iy;
    if (imgRatio > canRatio) {
      iw = w; ih = Math.round(w / imgRatio);
      ix = 0; iy = Math.round((h - ih) / 2);
    } else {
      ih = h; iw = Math.round(h * imgRatio);
      iy = 0; ix = Math.round((w - iw) / 2);
    }
    this.imgRect = { x: ix, y: iy, w: iw, h: ih };
  }

  _initCrop() {
    const { x, y, w, h } = this.imgRect;
    this.crop = { x, y, w, h };
  }

  _draw() {
    const { canvas, ctx, imgRect, crop } = this;
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 1. Image
    ctx.drawImage(this.image, imgRect.x, imgRect.y, imgRect.w, imgRect.h);

    // 2. Dark overlay around crop
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(imgRect.x, imgRect.y, imgRect.w, crop.y - imgRect.y);
    ctx.fillRect(imgRect.x, crop.y + crop.h, imgRect.w, (imgRect.y + imgRect.h) - (crop.y + crop.h));
    ctx.fillRect(imgRect.x, crop.y, crop.x - imgRect.x, crop.h);
    ctx.fillRect(crop.x + crop.w, crop.y, (imgRect.x + imgRect.w) - (crop.x + crop.w), crop.h);

    // 3. Crop border
    ctx.strokeStyle = "rgba(255,255,255,1)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(crop.x, crop.y, crop.w, crop.h);

    // 4. Rule-of-thirds grid
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.lineWidth = 1;
    for (let i = 1; i <= 2; i++) {
      const xLine = crop.x + (crop.w * i) / 3;
      ctx.beginPath(); ctx.moveTo(xLine, crop.y); ctx.lineTo(xLine, crop.y + crop.h); ctx.stroke();
      const yLine = crop.y + (crop.h * i) / 3;
      ctx.beginPath(); ctx.moveTo(crop.x, yLine); ctx.lineTo(crop.x + crop.w, yLine); ctx.stroke();
    }

    // 5. Corner handles
    ctx.fillStyle = "#ffffff";
    const hs = 10;
    [
      { x: crop.x,           y: crop.y },
      { x: crop.x + crop.w,  y: crop.y },
      { x: crop.x,           y: crop.y + crop.h },
      { x: crop.x + crop.w,  y: crop.y + crop.h },
    ].forEach(({ x, y }) => ctx.fillRect(x - hs / 2, y - hs / 2, hs, hs));

    // 6. Scale reference line
    this._drawScaleLine();

    // 7. Scale bar
    this._drawScaleBar();

    // 8. Reposition dimension overlays
    this._updateDimOverlays();
  }

  _drawScaleLine() {
    const { ctx, scalePt1, scalePt2, scalePreview } = this;
    if (!scalePt1) return;
    const end = scalePt2 ?? scalePreview ?? scalePt1;

    ctx.save();
    ctx.strokeStyle = "#ffcc00";
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 3]);
    ctx.beginPath();
    ctx.moveTo(scalePt1.x, scalePt1.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.setLineDash([]);

    [scalePt1, scalePt2].filter(Boolean).forEach(pt => {
      ctx.fillStyle = "#ffcc00";
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 5, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.restore();
  }

  _drawScaleBar() {
    const { ctx, crop, pxPerMeter } = this;
    if (!pxPerMeter || pxPerMeter <= 0) return;

    const maxPx = crop.w * 0.4;
    const candidates = [10, 5, 2, 1, 0.5, 0.25, 0.1];
    const chosen = candidates.find(v => pxPerMeter * v <= maxPx) ?? candidates[candidates.length - 1];
    const barLen = pxPerMeter * chosen;
    if (barLen < 4) return;
    const label = chosen >= 1 ? `${chosen} m` : `${chosen * 100} cm`;

    const x = crop.x + 10;
    const y = crop.y + crop.h - 14;
    if (x + barLen + 5 > crop.x + crop.w) return;

    ctx.save();
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(x - 4, y - 12, barLen + 8, 20);
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y); ctx.lineTo(x + barLen, y);
    ctx.moveTo(x, y - 4); ctx.lineTo(x, y + 4);
    ctx.moveTo(x + barLen, y - 4); ctx.lineTo(x + barLen, y + 4);
    ctx.stroke();
    ctx.fillStyle = "#fff";
    ctx.font = "10px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(label, x + barLen / 2, y + 12);
    ctx.restore();
  }

  _updateDimOverlays() {
    const { canvas, crop, pxPerMeter } = this;
    if (!canvas || !this.image || !pxPerMeter) {
      document.getElementById("floor-crop-dim-w").style.display = "none";
      document.getElementById("floor-crop-dim-h").style.display = "none";
      return;
    }

    const realW = (crop.w / pxPerMeter).toFixed(2);
    const realH = (crop.h / pxPerMeter).toFixed(2);

    const rect = canvas.getBoundingClientRect();
    const sx = rect.width  / canvas.width;
    const sy = rect.height / canvas.height;
    const ox = canvas.offsetLeft;
    const oy = canvas.offsetTop;
    const wrapper = canvas.parentElement;

    const dimW = document.getElementById("floor-crop-dim-w");
    if (dimW) {
      dimW.textContent = realW + " m";
      const cx = ox + (crop.x + crop.w / 2) * sx;
      const belowY = oy + (crop.y + crop.h + 8) * sy;
      const top = (belowY + 24 <= wrapper.clientHeight) ? belowY : oy + (crop.y + crop.h - 28) * sy;
      dimW.style.left = cx + "px";
      dimW.style.top  = top + "px";
      dimW.style.transform = "translateX(-50%)";
      dimW.style.display = "block";
    }

    const dimH = document.getElementById("floor-crop-dim-h");
    if (dimH) {
      dimH.textContent = realH + " m";
      const cy = oy + (crop.y + crop.h / 2) * sy;
      const rightX = ox + (crop.x + crop.w + 8) * sx;
      const left = (rightX + 55 <= wrapper.clientWidth) ? rightX : ox + (crop.x + crop.w - 60) * sx;
      dimH.style.left = left + "px";
      dimH.style.top  = cy + "px";
      dimH.style.transform = "translateY(-50%)";
      dimH.style.display = "block";
    }
  }

  // ── Scale mode ────────────────────────────────────────────────────────────

  _updateApplyBtn() {
    const btn = document.getElementById("floor-crop-apply");
    if (btn) btn.disabled = !this.pxPerMeter;
  }

  _enterScaleMode() {
    this.scaleMode    = true;
    this.scalePt1     = null;
    this.scalePt2     = null;
    this.scalePreview = null;
    const btn = document.getElementById("floor-crop-set-scale");
    if (btn) { btn.textContent = "✕ " + this.t("designerFloorCropCancelScale", "Cancel"); btn.classList.add("active"); }
    this._setSubtitle(this.t("designerFloorCropScaleStart", "Click a start point on a known distance"));
  }

  _exitScaleMode() {
    this.scaleMode    = false;
    this.scalePreview = null;
    const btn = document.getElementById("floor-crop-set-scale");
    if (btn) btn.classList.remove("active");
  }

  _resetScale() {
    this._exitScaleMode();
    this.scalePt1   = null;
    this.scalePt2   = null;
    this.pxPerMeter = null;
    this._hideScaleInput();
    const btn = document.getElementById("floor-crop-set-scale");
    if (btn) btn.textContent = this.t("designerFloorCropSetScale", "Set scale");
    this._setSubtitle(this.t("designerFloorCropScaleRequired", "Set a scale reference to continue"));
    this._updateApplyBtn();
  }

  _setSubtitle(text) {
    const el = this.modal?.querySelector(".modal-subtitle");
    if (el) el.textContent = text;
  }

  _showScaleInput() {
    const ref = document.getElementById("floor-crop-scale-ref");
    if (!ref || !this.scalePt1 || !this.scalePt2) return;

    const rect = this.canvas.getBoundingClientRect();
    const sx = rect.width  / this.canvas.width;
    const sy = rect.height / this.canvas.height;
    const ox = this.canvas.offsetLeft;
    const oy = this.canvas.offsetTop;

    const mx = ((this.scalePt1.x + this.scalePt2.x) / 2) * sx + ox;
    const my = ((this.scalePt1.y + this.scalePt2.y) / 2) * sy + oy;
    ref.style.left = Math.max(0, mx - 70) + "px";
    ref.style.top  = Math.max(0, my - 18) + "px";
    ref.style.display = "flex";

    const inp = document.getElementById("floor-crop-scale-len");
    if (inp) { inp.value = ""; inp.focus(); }
  }

  _hideScaleInput() {
    const ref = document.getElementById("floor-crop-scale-ref");
    if (ref) ref.style.display = "none";
    const inp = document.getElementById("floor-crop-scale-len");
    if (inp) inp.value = "";
  }

  _confirmScale() {
    const meters = parseFloat(document.getElementById("floor-crop-scale-len")?.value);
    if (!meters || meters <= 0 || !this.scalePt1 || !this.scalePt2) {
      this._resetScale();
      this._draw();
      return;
    }
    const dx = this.scalePt2.x - this.scalePt1.x;
    const dy = this.scalePt2.y - this.scalePt1.y;
    this.pxPerMeter = Math.sqrt(dx * dx + dy * dy) / meters;
    this._hideScaleInput();
    this._exitScaleMode();
    const btn = document.getElementById("floor-crop-set-scale");
    if (btn) btn.textContent = "⟳ " + this.t("designerFloorCropResetScale", "Reset scale");
    this._setSubtitle(this.t("designerFloorCropScaleAdjust", "Adjust crop to define the floor area"));
    this._updateApplyBtn();
    this._draw();
  }

  // ── Pointer handling ──────────────────────────────────────────────────────

  _getHandle(cx, cy) {
    const { crop } = this;
    const R = 12;
    const corners = {
      nw: { x: crop.x,           y: crop.y },
      ne: { x: crop.x + crop.w,  y: crop.y },
      sw: { x: crop.x,           y: crop.y + crop.h },
      se: { x: crop.x + crop.w,  y: crop.y + crop.h },
    };
    for (const [name, pt] of Object.entries(corners)) {
      if (Math.abs(cx - pt.x) <= R && Math.abs(cy - pt.y) <= R) return name;
    }
    return null;
  }

  _insideCrop(cx, cy) {
    const { crop } = this;
    return cx >= crop.x && cx <= crop.x + crop.w && cy >= crop.y && cy <= crop.y + crop.h;
  }

  _clampCrop(c) {
    const { imgRect } = this;
    const MIN = 20;
    c.x = Math.max(imgRect.x, Math.min(c.x, imgRect.x + imgRect.w - MIN));
    c.y = Math.max(imgRect.y, Math.min(c.y, imgRect.y + imgRect.h - MIN));
    c.w = Math.max(MIN, Math.min(c.w, imgRect.x + imgRect.w - c.x));
    c.h = Math.max(MIN, Math.min(c.h, imgRect.y + imgRect.h - c.y));
    return c;
  }

  _pointerPos(e) {
    const rect = this.canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (this.canvas.width / rect.width),
      y: (clientY - rect.top)  * (this.canvas.height / rect.height),
    };
  }

  _onPointerDown(e) {
    if (!this.image) return;
    e.preventDefault();
    const { x, y } = this._pointerPos(e);

    // Scale mode: collect reference points
    if (this.scaleMode) {
      if (!this.scalePt1) {
        this.scalePt1 = { x, y };
        this._setSubtitle(this.t("designerFloorCropScaleEnd", "Click the end point of the known distance"));
      } else {
        this.scalePt2 = { x, y };
        this.scalePreview = null;
        this._exitScaleMode();
        this._showScaleInput();
      }
      this._draw();
      return;
    }

    // Normal crop mode
    const handle = this._getHandle(x, y);
    if (handle) {
      this.drag = { mode: handle, startX: x, startY: y, origCrop: { ...this.crop } };
    } else if (this._insideCrop(x, y)) {
      this.drag = { mode: "move", startX: x, startY: y, origCrop: { ...this.crop } };
    } else {
      this.drag = { mode: "new", startX: x, startY: y, origCrop: null };
      this.crop = { x, y, w: 1, h: 1 };
    }
  }

  _onPointerMove(e) {
    // Scale mode preview line
    if (this.scaleMode && this.scalePt1 && !this.scalePt2) {
      e.preventDefault();
      this.scalePreview = this._pointerPos(e);
      this._draw();
      return;
    }

    if (!this.drag) return;
    e.preventDefault();
    const { x, y } = this._pointerPos(e);
    const dx = x - this.drag.startX;
    const dy = y - this.drag.startY;
    const orig = this.drag.origCrop;
    const { imgRect } = this;

    if (this.drag.mode === "move") {
      let nx = orig.x + dx;
      let ny = orig.y + dy;
      nx = Math.max(imgRect.x, Math.min(nx, imgRect.x + imgRect.w - orig.w));
      ny = Math.max(imgRect.y, Math.min(ny, imgRect.y + imgRect.h - orig.h));
      this.crop = { x: nx, y: ny, w: orig.w, h: orig.h };
    } else if (this.drag.mode === "new") {
      const sx = this.drag.startX;
      const sy = this.drag.startY;
      this.crop = this._clampCrop({
        x: Math.min(sx, x), y: Math.min(sy, y),
        w: Math.abs(x - sx), h: Math.abs(y - sy),
      });
    } else {
      let { x: cx, y: cy, w: cw, h: ch } = orig;
      const right = cx + cw, bottom = cy + ch;
      const mode = this.drag.mode;
      let nx, ny, nw, nh;
      if (mode === "nw") { nx = orig.x + dx; ny = orig.y + dy; nw = right - nx; nh = bottom - ny; }
      else if (mode === "ne") { nx = orig.x; ny = orig.y + dy; nw = orig.w + dx; nh = bottom - ny; }
      else if (mode === "sw") { nx = orig.x + dx; ny = orig.y; nw = right - nx; nh = orig.h + dy; }
      else /* se */ { nx = orig.x; ny = orig.y; nw = orig.w + dx; nh = orig.h + dy; }
      this.crop = this._clampCrop({ x: nx, y: ny, w: nw, h: nh });
    }
    this._draw();
  }

  _onPointerUp(e) {
    if (!this.drag) return;
    e.preventDefault();
    this.drag = null;
    this._draw();
  }

  _cropToDataURL() {
    const { imgRect, crop, image } = this;
    const scaleX = image.naturalWidth  / imgRect.w;
    const scaleY = image.naturalHeight / imgRect.h;
    const srcX = Math.round((crop.x - imgRect.x) * scaleX);
    const srcY = Math.round((crop.y - imgRect.y) * scaleY);
    const srcW = Math.round(crop.w * scaleX);
    const srcH = Math.round(crop.h * scaleY);
    const offscreen = document.createElement("canvas");
    offscreen.width  = srcW;
    offscreen.height = srcH;
    offscreen.getContext("2d").drawImage(image, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
    return offscreen.toDataURL("image/jpeg", 0.92);
  }

  _bindEvents() {
    if (!this.canvas) return;

    this.canvas.addEventListener("mousedown",  (e) => this._onPointerDown(e));
    this.canvas.addEventListener("mousemove",  (e) => this._onPointerMove(e));
    this.canvas.addEventListener("mouseup",    (e) => this._onPointerUp(e));
    this.canvas.addEventListener("mouseleave", (e) => this._onPointerUp(e));
    this.canvas.addEventListener("touchstart", (e) => this._onPointerDown(e), { passive: false });
    this.canvas.addEventListener("touchmove",  (e) => this._onPointerMove(e), { passive: false });
    this.canvas.addEventListener("touchend",   (e) => this._onPointerUp(e),   { passive: false });

    document.getElementById("floor-crop-set-scale")?.addEventListener("click", () => {
      if (this.scaleMode) {
        this._resetScale();
        this._draw();
      } else if (this.pxPerMeter) {
        this._resetScale();
        this._draw();
      } else {
        this._enterScaleMode();
      }
    });

    document.getElementById("floor-crop-scale-ok")?.addEventListener("click", () => this._confirmScale());
    document.getElementById("floor-crop-scale-len")?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") this._confirmScale();
      if (e.key === "Escape") { this._resetScale(); this._draw(); }
    });

    document.getElementById("floor-crop-apply")?.addEventListener("click", () => {
      if (!this.image) return;
      const dataURL = this._cropToDataURL();
      const realW = this.pxPerMeter ? this.crop.w / this.pxPerMeter : null;
      const realH = this.pxPerMeter ? this.crop.h / this.pxPerMeter : null;
      this.close();
      this.onApply(dataURL, realW, realH);
    });

    document.getElementById("floor-crop-cancel")?.addEventListener("click", () => this.close());
  }
}
